<script lang="ts" setup>
import type { HTMLAttributes } from "vue"
import { ref } from "vue"
import { cn } from "@/lib/utils"
import { useCollapsingBreadcrumb } from "@/composables/useCollapsingBreadcrumb"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

const props = defineProps<{
  class?: HTMLAttributes["class"]
}>()

// One line, never wrapped: crumbs keep their width and are hidden from the left when there's no room
// (`data-collapsed`, set by useCollapsingBreadcrumb); only the current page's item shrinks, cutting
// its text with an ellipsis once it's all that's left. Whenever something is hidden or cut, hovering
// the trail shows it whole.
const listRef = ref<HTMLElement | null>(null)
const { collapsed, trail } = useCollapsingBreadcrumb(listRef)
</script>

<template>
  <TooltipProvider>
    <Tooltip :disabled="!collapsed">
      <TooltipTrigger as="div" class="min-w-0">
        <ol
          ref="listRef"
          data-slot="breadcrumb-list"
          :class="cn('text-muted-foreground flex min-w-0 flex-nowrap items-center gap-1.5 text-sm sm:gap-2.5 [&>*]:shrink-0 [&>:has([aria-current=page])]:min-w-0 [&>:has([aria-current=page])]:shrink [&>[data-collapsed]]:hidden', props.class)"
        >
          <slot />
        </ol>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start">
        {{ trail }}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
